import {
  Component,
  DestroyRef,
  inject,
  Injector,
  viewChild,
  ElementRef,
  AfterViewInit,
  OnInit,
  signal,
  ViewContainerRef,
  computed,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { ReactiveFormsModule, FormsModule, Validators, FormBuilder, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule, MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { PopoverPositions } from '@vendasta/galaxy/popover';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BreakpointObserver } from '@angular/cdk/layout';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyRichTextEditorModule, RichTextEditorComponent } from '@vendasta/galaxy/rich-text-editor';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';
import {
  SendApiService,
  SendFromProviderRequest,
  SendFromProviderRequestCrmObjectAssociation,
  Provider,
  UserParticipant,
  ContactParticipant,
  EmailParticipant,
  EmailRecipient,
  WhiteLabelEmailMessage,
  URLAttachment,
  Visibility,
  SourceInfo,
} from '@vendasta/email';
import { FilesApiService, CreateFileRequest, FileAccessType, CreateFileResponseInterface } from '@vendasta/media';
import {
  GalaxyUploaderModule,
  GalaxyUploaderComponent,
  FileInfo,
  FileUploadStatus,
  FileUploadError,
  UploadHandler,
  UploadResponse,
} from '@vendasta/galaxy/uploader';
import { Contact, ContactProvider } from '@galaxy/email-ui/shared';
import { CrmFieldService, StandardExternalIds } from '@galaxy/crm/services/field-service';
import {
  SelectObjectTypeModalComponent,
  ListObjectTypeDialogData,
  ListObjectTypeDialogResult,
  PageAnalyticsInjectionToken,
  tableFiltersInjectionTokenGenerator,
  pageAnalyticsInjectionTokenGenerator,
} from '@galaxy/crm/static';
import { GalaxyFilterChipInjectionToken } from '@vendasta/galaxy/filter/chips';
import { Editor } from 'tinymce';
import {
  CrmDynamicFieldService,
  DYNAMIC_COMPONENT_DATA_TOKEN,
  TEMPLATE_HYDRATION_DATA_TOKEN,
  EMAIL_BUILDER_APP_LISTING_SERVICE_TOKEN,
  VariableMenuItem,
} from '@galaxy/email-ui/email-builder';
import { DynamicComponentSelectorService, DynamicComponentDialogComponent } from '@galaxy/email-ui/email-builder';
import {
  RICH_TEXT_PLUGINS_WITH_IMAGES,
  RICH_TEXT_TOOLBAR_WITH_IMAGES,
  INLINE_IMAGE_MAX_BYTES,
  ALLOWED_INLINE_IMAGE_MIME_TYPES,
  EmailProvider,
  EMAIL_PROVIDER_OUTLOOK,
} from './constants';
import { EmailComposerData, ProviderConnectionOption } from '../email-workflow.service';
import { SendEmailService, DraftInlineImage } from './send-email.service';
import { getSignature$ } from '@vendasta/protos/email/v1/send';
import { Provider as ProtoProvider } from '@vendasta/protos/email/v1/enums';

interface ContactInfo {
  id: string;
  name: string;
  email: string;
}

const MAX_RECIPIENTS = 100;
const DEFAULT_CONTENT_TYPE = 'application/octet-stream';
const BLOCKED_MIME_TYPES = ['application/x-rar-compressed', DEFAULT_CONTENT_TYPE];
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/;

interface AttachmentInfo {
  fileUrl: string;
  attachedFilename: string;
  size: number;
  status: FileUploadStatus;
}

@Component({
  selector: 'lib-crm-send-email',
  templateUrl: './send-email.component.html',
  styleUrls: ['./send-email.component.scss'],
  providers: [
    DynamicComponentSelectorService,
    { provide: DYNAMIC_COMPONENT_DATA_TOKEN, useValue: [] },
    { provide: TEMPLATE_HYDRATION_DATA_TOKEN, useValue: null },
    { provide: EMAIL_BUILDER_APP_LISTING_SERVICE_TOKEN, useValue: null },
  ],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatAutocompleteModule,
    GalaxyFormFieldModule,
    GalaxyTooltipModule,
    GalaxyRichTextEditorModule,
    GalaxyButtonLoadingIndicatorModule,
    GalaxyUploaderModule,
    MatProgressSpinnerModule,
  ],
})
export class CrmSendEmailComponent implements OnInit, AfterViewInit {
  private readonly dialogRef = inject(MatDialogRef<CrmSendEmailComponent>);
  private readonly dialogData = inject<EmailComposerData>(MAT_DIALOG_DATA);
  private readonly matDialog = inject(MatDialog);
  private readonly snackbarService = inject(SnackbarService);
  private readonly confirmationModalService = inject(OpenConfirmationModalService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly fb = inject(FormBuilder);
  private readonly sendEmailService = inject(SendApiService);
  private readonly draftService = inject(SendEmailService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly crmDynamicFieldService = inject(CrmDynamicFieldService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly dynamicComponentSelectorService = inject(DynamicComponentSelectorService);
  private readonly filesApiService = inject(FilesApiService);
  private readonly http = inject(HttpClient);

  private static readonly CRM_DYNAMIC_CATEGORIES: VariableMenuItem[] = [
    {
      textKey: 'EMAIL_BUILDER.DYNAMIC_COMPONENT_SELECTOR.CONTACT_DETAILS',
      replacementTags: ['crmCampaignContactFields'],
      options: { requiredParams: [], nestedDynamicSelector: true },
    },
    {
      textKey: 'EMAIL_BUILDER.DYNAMIC_COMPONENT_SELECTOR.PRIMARY_COMPANY_DETAILS',
      replacementTags: ['crmCampaignCompanyFields'],
      options: { requiredParams: [], nestedDynamicSelector: true },
    },
  ];

  protected readonly visibilityOptions = [
    { value: Visibility.VISIBILITY_PRIVATE, label: 'CRM.VISIBILITY_PRIVATE' },
    { value: Visibility.VISIBILITY_INTERNAL, label: 'CRM.VISIBILITY_INTERNAL' },
  ];

  protected readonly FileUploadStatus = FileUploadStatus;
  protected readonly MAX_TOTAL_ATTACHMENT_SIZE = 18 * 1024 * 1024;

  protected readonly tooltipPositions = [PopoverPositions['Right'], PopoverPositions['Top']];
  readonly subjectInput = viewChild<ElementRef<HTMLInputElement>>('subjectInput');
  private readonly richTextEditor = viewChild<RichTextEditorComponent>('richTextEditor');
  private readonly fileUploader = viewChild<GalaxyUploaderComponent>('fileUploader');

  protected readonly RICH_TEXT_PLUGINS_WITH_IMAGES = RICH_TEXT_PLUGINS_WITH_IMAGES;
  protected readonly RICH_TEXT_TOOLBAR_WITH_IMAGES = RICH_TEXT_TOOLBAR_WITH_IMAGES;

  protected readonly editorCustomSetup = (editor: Editor) => {
    editor.ui.registry.addButton('dynamicContentButton', {
      text: this.translateService.instant('EMAIL_BUILDER.DYNAMIC_COMPONENT_SELECTOR.ADD_DYNAMIC_CONTENT'),
      onAction: () => this.openDynamicContentDialog(),
    });

    editor.ui.registry.addButton('insertImage', {
      icon: 'image',
      tooltip: this.translateService.instant('CRM.INSERT_IMAGE'),
      onAction: () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = ALLOWED_INLINE_IMAGE_MIME_TYPES.join(',');
        input.style.position = 'fixed';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.onchange = () => {
          const file = input.files?.[0];
          document.body.removeChild(input);
          if (!file) return;
          this.uploadAndInsertImage(editor, file);
        };
        input.click();
      },
    });

    editor.on('NodeChange', () => this.replaceDataUriImages(editor));

    editor.on('focus', () => {
      setTimeout(() => this.recipientsCollapsed.set(true), 100);
    });
  };

  protected readonly showErrors = signal(false);
  protected readonly isLoading = signal(false);
  protected readonly isSending = signal(false);
  protected readonly contacts = signal<ContactInfo[]>([]);
  protected readonly recipientLimitError = signal(false);
  protected readonly attachments = signal<AttachmentInfo[]>([]);
  protected readonly inlineImages = signal<DraftInlineImage[]>([]);
  private readonly contentHtml = signal('');
  protected readonly usedBytes = computed(() => this.computeUsedBytes(this.contentHtml()));
  protected readonly totalAttachmentSize = computed(() => this.attachments().reduce((sum, a) => sum + a.size, 0));
  protected readonly hasUploadsInProgress = computed(() =>
    this.attachments().some((a) => a.status === FileUploadStatus.Queued || a.status === FileUploadStatus.InProgress),
  );
  private emailSent = false;
  private readonly destroyRef = inject(DestroyRef);
  private readonly MOBILE_BREAKPOINT = '(max-width: 599px)';

  protected readonly connections: ProviderConnectionOption[] = this.dialogData.connections || [];
  protected readonly selectedConnectionId = signal(this.connections[0]?.connectionId ?? '');

  protected readonly emailForm = this.fb.nonNullable.group({
    subject: ['', [Validators.required]],
    content: ['', [Validators.required]],
    visibility: [Visibility.VISIBILITY_INTERNAL],
  });

  protected readonly toChips = signal<string[]>([]);
  protected readonly showCc = signal(false);
  protected readonly showBcc = signal(false);
  protected readonly ccChips = signal<string[]>([]);
  protected readonly bccChips = signal<string[]>([]);
  protected readonly ccSearchResults = signal<Contact[]>([]);
  protected readonly bccSearchResults = signal<Contact[]>([]);
  protected readonly ccSearchControl = new FormControl<string>('', { nonNullable: true });
  protected readonly bccSearchControl = new FormControl<string>('', { nonNullable: true });
  protected readonly separatorKeyCodes = [ENTER, COMMA] as const;

  protected readonly recipientsCollapsed = signal(false);

  protected readonly collapsedRecipientSummary = computed(() => {
    const to = this.toChips();
    const cc = this.ccChips();
    const bcc = this.bccChips();

    const parts: string[] = [];

    if (to.length === 1) {
      parts.push(`To: ${to[0]}`);
    } else if (to.length > 1) {
      parts.push(`To: ${to[0]}, +${to.length - 1} more`);
    }

    if (cc.length > 0 && this.showCc()) {
      parts.push(`Cc: ${cc[0]}${cc.length > 1 ? `, +${cc.length - 1} more` : ''}`);
    }

    if (bcc.length > 0 && this.showBcc()) {
      parts.push(`Bcc: ${bcc[0]}${bcc.length > 1 ? `, +${bcc.length - 1} more` : ''}`);
    }

    return parts.join(', ');
  });

  private readonly injector = inject(Injector);
  private readonly crmFieldService = inject(CrmFieldService, { optional: true });

  private readonly contactProvider: ContactProvider | undefined = this.dialogData.contactProvider;

  constructor() {
    if (this.dialogData?.contactIds?.length) {
      this.loadAndPopulateContacts(this.dialogData.contactIds, this.dialogData.emails || []);
    }

    if (this.dialogData?.replySubject) {
      this.emailForm.patchValue({ subject: this.dialogData.replySubject });
    }

    if (this.dialogData?.replyVisibility) {
      const v = this.dialogData.replyVisibility.toUpperCase();
      if (v === 'PRIVATE') {
        this.emailForm.patchValue({ visibility: Visibility.VISIBILITY_PRIVATE });
      } else if (v === 'INTERNAL') {
        this.emailForm.patchValue({ visibility: Visibility.VISIBILITY_INTERNAL });
      }
    }

    if (this.dialogData?.ccEmails?.length) {
      const ccContactIds = this.dialogData.ccContactIds || [];
      const validCcEmails: string[] = [];
      this.dialogData.ccEmails.forEach((email, index) => {
        if (!email?.trim()) return;
        validCcEmails.push(email);
        const contactId = ccContactIds[index];
        if (contactId) {
          this.contacts.update((existing) => {
            if (existing.some((c) => c.id === contactId)) return existing;
            return [...existing, { id: contactId, name: email, email }];
          });
        }
      });
      if (validCcEmails.length > 0) {
        this.ccChips.set(validCcEmails);
        this.showCc.set(true);
      }
    }

    this.setupAutocomplete();
    this.emailForm.controls.content.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(150))
      .subscribe((html) => this.contentHtml.set(html || ''));
    this.destroyRef.onDestroy(() => this.saveDraftIfNeeded());
  }

  ngOnInit(): void {
    this.dynamicComponentSelectorService.overrideParentVariableCategories(CrmSendEmailComponent.CRM_DYNAMIC_CATEGORIES);
    this.breakpointObserver
      .observe(this.MOBILE_BREAKPOINT)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        this.updateDialogSize(state.matches);
      });

    this.loadSignature();
  }

  ngAfterViewInit() {
    const inputElement = this.subjectInput();
    if (inputElement) {
      setTimeout(() => {
        inputElement.nativeElement.focus();
      }, 0);
    }
  }

  private setupAutocomplete(): void {
    const provider = this.contactProvider;
    if (!provider) {
      return;
    }
    this.bindContactSearch(provider, this.ccSearchControl, this.ccSearchResults);
    this.bindContactSearch(provider, this.bccSearchControl, this.bccSearchResults);
  }

  private bindContactSearch(
    provider: ContactProvider,
    control: FormControl<string>,
    results: typeof this.ccSearchResults,
  ): void {
    control.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          if (!term || term.length < 2) {
            return of([]);
          }
          return provider.searchContacts(term, '', 10).pipe(
            map((result) => result.contacts),
            catchError(() => of([])),
          );
        }),
        map((contacts) => this.filterExistingRecipients(contacts)),
      )
      .subscribe((contacts) => results.set(contacts));
  }

  private filterExistingRecipients(contacts: Contact[]): Contact[] {
    const existing = new Set([
      ...this.toChips().map((e) => e.toLowerCase()),
      ...this.ccChips().map((e) => e.toLowerCase()),
      ...this.bccChips().map((e) => e.toLowerCase()),
    ]);
    return contacts.filter((c) => c.email && !existing.has(c.email.toLowerCase()));
  }

  private isInTarget(email: string, target: 'to' | 'cc' | 'bcc'): boolean {
    const lower = email.toLowerCase();
    const chips = target === 'to' ? this.toChips() : target === 'cc' ? this.ccChips() : this.bccChips();
    return chips.some((e) => e.toLowerCase() === lower);
  }

  private findEmailSource(email: string): 'to' | 'cc' | 'bcc' | null {
    const lower = email.toLowerCase();
    if (this.toChips().some((e) => e.toLowerCase() === lower)) return 'to';
    if (this.ccChips().some((e) => e.toLowerCase() === lower)) return 'cc';
    if (this.bccChips().some((e) => e.toLowerCase() === lower)) return 'bcc';
    return null;
  }

  private tryMoveOrAdd(email: string, target: 'to' | 'cc' | 'bcc'): boolean {
    if (this.isInTarget(email, target)) {
      return false;
    }
    const lower = email.toLowerCase();
    const source = this.findEmailSource(email);
    if (source) {
      if (source === 'to' && this.toChips().length <= 1) {
        this.snackbarService.openErrorSnack('CRM.CANNOT_MOVE_LAST_TO_RECIPIENT');
        return false;
      }
      const removeFilter = (e: string) => e.toLowerCase() !== lower;
      if (source === 'to') this.toChips.update((c) => c.filter(removeFilter));
      else if (source === 'cc') this.ccChips.update((c) => c.filter(removeFilter));
      else this.bccChips.update((c) => c.filter(removeFilter));
    }
    if (target === 'to') this.toChips.update((c) => [...c, email]);
    else if (target === 'cc') this.ccChips.update((c) => [...c, email]);
    else this.bccChips.update((c) => [...c, email]);
    return true;
  }

  protected removeToChip(email: string): void {
    if (this.toChips().length <= 1) {
      return;
    }
    this.toChips.update((chips) => chips.filter((c) => c !== email));
    this.recipientLimitError.set(false);
  }

  protected canRemoveToChip(): boolean {
    return this.toChips().length > 1;
  }

  protected openContactSelector(target: 'to' | 'cc' | 'bcc'): void {
    if (this.isSending()) {
      return;
    }

    const modalInjector = Injector.create({
      providers: [
        {
          provide: PageAnalyticsInjectionToken,
          useFactory: pageAnalyticsInjectionTokenGenerator('Contact'),
        },
        {
          provide: GalaxyFilterChipInjectionToken,
          useFactory: tableFiltersInjectionTokenGenerator('Contact', undefined, false),
        },
      ],
      parent: this.injector,
    });

    const ref = this.matDialog.open(SelectObjectTypeModalComponent, {
      data: {
        baseColumnIds: [],
        objectType: 'Contact',
        modalTitle: this.translateService.instant('CRM.SELECT_CONTACTS'),
        associations: [],
        associationType: '',
        newSelection: '',
        hideCreateButton: true,
        showActions: false,
        hideActionButton: true,
      } as ListObjectTypeDialogData,
      width: '900px',
      maxHeight: '80vh',
      autoFocus: 'input',
      injector: modalInjector,
    });

    ref.afterClosed().subscribe((result: ListObjectTypeDialogResult | undefined) => {
      if (!result?.selectedRows?.length) {
        return;
      }
      const emailFieldId = this.crmFieldService?.getFieldId(StandardExternalIds.Email) ?? '';
      for (const row of result.selectedRows) {
        const email = row.data[emailFieldId]?.value || '';
        if (!email) {
          continue;
        }
        if (this.tryMoveOrAdd(email, target)) {
          this.contacts.update((existing) => {
            if (existing.some((c) => c.email === email)) {
              return existing;
            }
            return [...existing, { id: row.id, name: row.data['fullName']?.value || row.id, email }];
          });
        }
      }
      this.recipientLimitError.set(false);
    });
  }

  protected toggleCc(): void {
    this.showCc.update((v) => !v);
    if (!this.showCc()) {
      this.ccChips.set([]);
      this.ccSearchControl.setValue('');
    }
  }

  protected toggleBcc(): void {
    this.showBcc.update((v) => !v);
    if (!this.showBcc()) {
      this.bccChips.set([]);
      this.bccSearchControl.setValue('');
    }
  }

  protected addCcChip(event: MatChipInputEvent): void {
    const email = (event.value || '').trim();
    if (email && EMAIL_REGEX.test(email)) {
      this.tryMoveOrAdd(email, 'cc');
    }
    event.chipInput.clear();
    this.ccSearchControl.setValue('');
    this.recipientLimitError.set(false);
  }

  protected removeCcChip(email: string): void {
    this.ccChips.update((chips) => chips.filter((c) => c !== email));
    this.recipientLimitError.set(false);
  }

  protected selectCcContact(event: MatAutocompleteSelectedEvent): void {
    const contact = event.option.value as Contact;
    if (contact.email) {
      this.tryMoveOrAdd(contact.email, 'cc');
    }
    this.ccSearchControl.setValue('');
  }

  protected addBccChip(event: MatChipInputEvent): void {
    const email = (event.value || '').trim();
    if (email && EMAIL_REGEX.test(email)) {
      this.tryMoveOrAdd(email, 'bcc');
    }
    event.chipInput.clear();
    this.bccSearchControl.setValue('');
    this.recipientLimitError.set(false);
  }

  protected removeBccChip(email: string): void {
    this.bccChips.update((chips) => chips.filter((c) => c !== email));
    this.recipientLimitError.set(false);
  }

  protected selectBccContact(event: MatAutocompleteSelectedEvent): void {
    const contact = event.option.value as Contact;
    if (contact.email) {
      this.tryMoveOrAdd(contact.email, 'bcc');
    }
    this.bccSearchControl.setValue('');
  }

  private getTotalRecipientCount(): number {
    return this.toChips().length + this.ccChips().length + this.bccChips().length;
  }

  private updateDialogSize(isMobile: boolean): void {
    if (isMobile) {
      this.dialogRef.updateSize('100vw', '100vh');
    } else {
      this.dialogRef.updateSize('1000px', '95vh');
      this.dialogRef.updatePosition();
    }
  }

  private async loadAndPopulateContacts(contactIds: string[], emailAddresses: string[]): Promise<void> {
    try {
      this.isLoading.set(true);

      this.contacts.set(
        contactIds.map((id, index) => ({
          id,
          name: `Contact ${index + 1}`,
          email: emailAddresses[index] || '',
        })),
      );

      const validEmails = this.contacts()
        .map((contact) => contact.email)
        .filter((email) => email && email.trim() !== '');

      if (validEmails.length === 0) {
        await firstValueFrom(
          this.confirmationModalService.openModal({
            type: 'warn',
            title: 'CRM.NO_EMAIL_ADDRESS_TITLE',
            message: 'CRM.NO_EMAIL_ADDRESS_MESSAGE',
            confirmButtonText: 'COMMON.ACTION_LABELS.CLOSE',
            cancelButtonText: undefined,
            cancelOnEscapeKeyOrBackgroundClick: true,
          }),
        );
        this.closeModal();
        return;
      }

      this.toChips.set(validEmails);

      if (!this.dialogData?.isReply) {
        await this.checkAndRestoreDraft(validEmails.join(', '));
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
      this.snackbarService.openErrorSnack('CRM.ERROR_LOADING_CONTACTS');
    } finally {
      this.isLoading.set(false);
    }
  }

  private async checkAndRestoreDraft(to: string): Promise<void> {
    const draft = this.draftService.getDraft(to);
    if (!draft) {
      return;
    }

    const shouldRestore = await firstValueFrom(this.draftService.askToRestoreDraft(to));

    if (shouldRestore) {
      const restoredContent = await this.crmDynamicFieldService.convertTemplateTagToHumanReadable(draft.content);
      this.emailForm.patchValue({
        subject: draft.subject,
        content: restoredContent,
      });
      if (draft.cc.length > 0) {
        this.ccChips.set(draft.cc);
        this.showCc.set(true);
      }
      if (draft.bcc.length > 0) {
        this.bccChips.set(draft.bcc);
        this.showBcc.set(true);
      }
      if (draft.attachments?.length > 0) {
        this.attachments.set(
          draft.attachments.map((a) => ({
            ...a,
            status: FileUploadStatus.Success,
          })),
        );
      }
      if (draft.inlineImages?.length > 0) {
        this.inlineImages.set(draft.inlineImages);
      }
    } else {
      this.draftService.deleteDraft(to);
    }
  }

  private saveDraftIfNeeded(): void {
    if (this.emailSent) {
      return;
    }

    const toValue = this.toChips().join(', ');
    const subject = this.emailForm.value.subject || '';
    const content = this.emailForm.value.content || '';
    const contentWithTags = this.crmDynamicFieldService.convertHumanReadableToTemplateTag(content);
    const successfulAttachments = this.attachments()
      .filter((a) => a.status === FileUploadStatus.Success)
      .map(({ fileUrl, attachedFilename, size }) => ({ fileUrl, attachedFilename, size }));
    const inlineImages = this.inlineImages().map(({ fileUrl, size }) => ({ fileUrl, size }));

    if (
      toValue &&
      (subject.trim() ||
        contentWithTags.trim() ||
        this.ccChips().length > 0 ||
        this.bccChips().length > 0 ||
        successfulAttachments.length > 0 ||
        inlineImages.length > 0)
    ) {
      this.draftService.saveDraft(
        toValue,
        subject,
        contentWithTags,
        this.ccChips(),
        this.bccChips(),
        successfulAttachments,
        inlineImages,
      );
    }
  }

  protected async onSend() {
    if (this.toChips().length === 0) {
      this.showErrors.set(true);
      return;
    }

    if (this.hasUploadsInProgress()) {
      return;
    }

    if (!this.emailForm.valid) {
      this.showErrors.set(true);
      this.emailForm.markAllAsTouched();
      return;
    }

    if (this.getTotalRecipientCount() > MAX_RECIPIENTS) {
      this.recipientLimitError.set(true);
      return;
    }

    if (!this.isSending()) {
      try {
        this.isSending.set(true);

        const formValue = this.emailForm.getRawValue();
        const contentWithTags = this.crmDynamicFieldService.convertHumanReadableToTemplateTag(formValue.content);
        await this.sendEmailWithProvider(
          formValue.subject,
          contentWithTags,
          this.dialogData.provider,
          formValue.visibility,
        );

        this.emailSent = true;
        const toValue = this.toChips().join(', ');
        if (toValue) {
          this.draftService.deleteDraft(toValue);
        }

        this.snackbarService.openSuccessSnack('CRM.EMAIL_SENT_SUCCESS');
        this.closeModal();
      } catch (error) {
        console.error('Error sending email:', error);
        this.snackbarService.openErrorSnack('CRM.ERROR_SENDING_EMAIL');
      } finally {
        this.isSending.set(false);
      }
    }
  }

  private async sendEmailWithProvider(
    subject: string,
    content: string,
    provider: EmailProvider,
    visibility: Visibility,
  ): Promise<void> {
    const request = this.buildSendFromProviderRequest(
      this.toChips(),
      this.ccChips(),
      this.bccChips(),
      this.dialogData.namespace,
      this.dialogData.userId,
      subject,
      content,
      provider,
      visibility,
    );
    await firstValueFrom(this.sendEmailService.sendFromProvider(request));
  }

  private buildEmailRecipients(emails: string[]): EmailRecipient[] {
    return emails
      .filter((e) => e)
      .map((email) => {
        const contact = this.contacts().find((c) => c.email === email);
        if (contact) {
          return new EmailRecipient({
            contactParticipant: new ContactParticipant({
              contactId: contact.id,
              namespace: this.dialogData.namespace,
            }),
          });
        }
        return new EmailRecipient({
          emailParticipant: new EmailParticipant({ email }),
        });
      });
  }

  private buildSendFromProviderRequest(
    toEmails: string[],
    ccEmails: string[],
    bccEmails: string[],
    namespace: string,
    currentUserId: string,
    subject: string,
    htmlContent: string,
    provider: EmailProvider,
    visibility: Visibility,
  ): SendFromProviderRequest {
    const userParticipant = new UserParticipant({
      userId: currentUserId,
    });

    let fullHtmlContent = htmlContent;
    if (this.dialogData?.replyBody) {
      fullHtmlContent += `<br><br><blockquote style="border-left:2px solid #ccc;padding-left:8px;margin-left:0;color:#555">${this.dialogData.replyBody}</blockquote>`;
    }

    const message = new WhiteLabelEmailMessage({
      subject: subject,
      htmlContent: fullHtmlContent,
      attributes: [],
      links: [],
      attachments: [],
      urlAttachments: this.attachments()
        .filter((a) => a.status === FileUploadStatus.Success)
        .map(
          (a) =>
            new URLAttachment({
              fileUrl: a.fileUrl,
              attachedFilename: a.attachedFilename,
            }),
        ),
    });

    const sourceInfo = new SourceInfo({
      sourceName: 'CRM UI',
      sourceDrillDown1: this.router.url,
    });

    const additionalAssociations = (this.dialogData.associations || [])
      .filter((a) => a.crmObjectId && a.crmObjectType)
      .map(
        (a) =>
          new SendFromProviderRequestCrmObjectAssociation({
            crmObjectId: a.crmObjectId,
            crmObjectType: a.crmObjectType,
            crmObjectSubtype: a.crmObjectSubtype,
            tags: a.tags,
          }),
      );

    return new SendFromProviderRequest({
      namespace: namespace,
      provider: provider === EMAIL_PROVIDER_OUTLOOK ? Provider.PROVIDER_OUTLOOK : Provider.PROVIDER_GMAIL,
      userParticipant: userParticipant,
      message: message,
      sendAsDraft: false,
      providerMessageThreadId: this.dialogData?.providerMessageThreadId || undefined,
      visibility: visibility,
      sourceInfo: sourceInfo,
      fromConnectionId: this.selectedConnectionId() || undefined,
      inReplyToMessageId: this.dialogData?.inReplyToMessageId || undefined,
      additionalAssociations: additionalAssociations.length > 0 ? additionalAssociations : undefined,
      toRecipients: this.buildEmailRecipients(toEmails),
      ccRecipients: this.buildEmailRecipients(ccEmails),
      bccRecipients: this.buildEmailRecipients(bccEmails),
    });
  }

  protected openDynamicContentDialog(): void {
    const dialogRef = this.matDialog.open(DynamicComponentDialogComponent, {
      autoFocus: false,
      width: '450px',
      height: '450px',
      viewContainerRef: this.viewContainerRef,
    });

    dialogRef
      .afterClosed()
      .pipe(
        map((result: string | null) => result || ''),
        switchMap((tag) => this.crmDynamicFieldService.convertTemplateTagToHumanReadable$(of(tag))),
        take(1),
      )
      .subscribe((humanReadable) => {
        if (humanReadable?.trim()) {
          this.richTextEditor()?.insertContent(humanReadable);
        }
      });
  }

  protected readonly uploadHandler: UploadHandler = (fileInfo: FileInfo): Observable<UploadResponse> => {
    if (!fileInfo.file) {
      return throwError(() => new Error('No file provided'));
    }

    const file = fileInfo.file;
    const contentType = file.type || DEFAULT_CONTENT_TYPE;

    if (BLOCKED_MIME_TYPES.includes(contentType)) {
      this.snackbarService.openErrorSnack('CRM.ATTACHMENT_TYPE_BLOCKED');
      return throwError(() => new Error('File type not allowed'));
    }

    const newTotal = this.totalAttachmentSize() + file.size;
    if (newTotal > this.MAX_TOTAL_ATTACHMENT_SIZE) {
      this.snackbarService.openErrorSnack('CRM.ATTACHMENT_SIZE_LIMIT_EXCEEDED');
      return throwError(() => new Error('Total attachment size exceeded'));
    }

    const createFileRequest = new CreateFileRequest({
      namespace: this.dialogData.namespace,
      fileMetadata: {
        fileName: file.name,
        path: 'email/out/attach',
        contentType,
        accessType: FileAccessType.PRIVATE,
      },
    });

    return this.filesApiService.createFile(createFileRequest).pipe(
      switchMap((response: CreateFileResponseInterface) => {
        if (!response.signedUrl) {
          throw new Error('No signed URL in response');
        }
        return this.http
          .put(response.signedUrl, file, {
            headers: { 'Content-Type': contentType },
            withCredentials: false,
          })
          .pipe(
            map(
              () =>
                ({
                  data: { url: response.fileUrl || '' },
                }) as UploadResponse,
            ),
          );
      }),
      catchError((error) => {
        console.error('Attachment upload failed:', error);
        return throwError(() => error);
      }),
    );
  };

  private readonly uploadingDataUris = new Set<string>();

  private replaceDataUriImages(editor: Editor): void {
    const doc = editor.getDoc();
    if (!doc) return;
    const images = doc.querySelectorAll('img[src^="data:"], img[src^="blob:"]');
    images.forEach((img) => {
      const src = img.getAttribute('src');
      if (!src || this.uploadingDataUris.has(src)) return;
      this.uploadingDataUris.add(src);

      const mimeMatch = src.match(/^data:(image\/[^;,]+)/);
      const mime = mimeMatch?.[1] || 'image/png';
      if (!ALLOWED_INLINE_IMAGE_MIME_TYPES.includes(mime)) {
        img.remove();
        this.uploadingDataUris.delete(src);
        return;
      }

      fetch(src)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], `pasted-image.${mime.split('/')[1]}`, { type: mime });
          const blobInfo = { blob: () => file, filename: () => file.name };
          return this.handleInlineImageUpload(blobInfo);
        })
        .then((url) => {
          img.setAttribute('src', url);
          this.uploadingDataUris.delete(src);
          editor.fire('change');
        })
        .catch(() => {
          img.remove();
          this.uploadingDataUris.delete(src);
          editor.fire('change');
        });
    });
  }

  private uploadAndInsertImage(editor: Editor, file: File): void {
    const blobInfo = {
      blob: () => file,
      filename: () => file.name,
    };
    this.handleInlineImageUpload(blobInfo)
      .then((url) => {
        editor.insertContent(`<img src="${url}" />`);
      })
      .catch(() => {
        this.snackbarService.openErrorSnack('CRM.INLINE_IMAGE_UPLOAD_FAILED');
      });
  }

  protected readonly handleInlineImageUpload = async (blobInfo: {
    blob: () => Blob;
    filename: () => string;
  }): Promise<string> => {
    const blob = blobInfo.blob();
    const filename = blobInfo.filename();
    const contentType = blob.type;

    if (!ALLOWED_INLINE_IMAGE_MIME_TYPES.includes(contentType)) {
      this.snackbarService.openErrorSnack('CRM.INLINE_IMAGE_TYPE_BLOCKED');
      throw new Error('MIME type not allowed');
    }

    if (blob.size > INLINE_IMAGE_MAX_BYTES) {
      this.snackbarService.openErrorSnack('CRM.INLINE_IMAGE_TOO_LARGE');
      throw new Error('Image exceeds 5 MB cap');
    }

    const currentHtml = this.emailForm.value.content || '';
    if (this.computeUsedBytes(currentHtml) + blob.size > this.MAX_TOTAL_ATTACHMENT_SIZE) {
      this.snackbarService.openErrorSnack('CRM.ATTACHMENT_SIZE_LIMIT_EXCEEDED');
      throw new Error('Total budget (18 MB) exceeded');
    }

    const request = new CreateFileRequest({
      namespace: this.dialogData.namespace,
      fileMetadata: {
        fileName: filename,
        path: 'email/out/embed',
        contentType,
        accessType: FileAccessType.PUBLIC,
      },
    });

    const response = await firstValueFrom(this.filesApiService.createFile(request));
    if (!response.signedUrl) {
      throw new Error('No signed URL in response');
    }
    await firstValueFrom(
      this.http.put(response.signedUrl, blob, {
        headers: { 'Content-Type': contentType },
        withCredentials: false,
      }),
    );
    const fileUrl = response.fileUrl || '';
    this.inlineImages.update((list) => [...list, { fileUrl, size: blob.size }]);
    return fileUrl;
  };

  protected browseForFiles(): void {
    this.fileUploader()?.browseForFiles();
  }

  protected onFileUploaded(fileInfo: FileInfo): void {
    this.syncAttachmentFromUploader(fileInfo);
  }

  protected onFileUploadErrored(error: FileUploadError): void {
    if (error.fileInfo) {
      this.syncAttachmentFromUploader(error.fileInfo);
    }
    this.snackbarService.openErrorSnack('CRM.ATTACHMENT_UPLOAD_FAILED');
  }

  protected onFilesChanged(files: FileInfo[]): void {
    const uploaderNames = new Set(files.map((f) => f.name));
    const preserved = this.attachments().filter((a) => !uploaderNames.has(a.attachedFilename));
    const fromUploader: AttachmentInfo[] = files.map((f) => ({
      fileUrl: f.url || '',
      attachedFilename: f.name,
      size: f.file?.size || 0,
      status: f.status ?? FileUploadStatus.Queued,
    }));
    this.attachments.set([...preserved, ...fromUploader]);
  }

  private syncAttachmentFromUploader(fileInfo: FileInfo): void {
    this.attachments.update((list) => {
      const index = list.findIndex((a) => a.attachedFilename === fileInfo.name);
      if (index === -1) {
        return [
          ...list,
          {
            fileUrl: fileInfo.url || '',
            attachedFilename: fileInfo.name,
            size: fileInfo.file?.size || 0,
            status: fileInfo.status ?? FileUploadStatus.Success,
          },
        ];
      }
      return list.map((a, i) =>
        i === index ? { ...a, fileUrl: fileInfo.url || a.fileUrl, status: fileInfo.status ?? a.status } : a,
      );
    });
  }

  protected removeAttachment(index: number): void {
    const target = this.attachments()[index];
    if (!target) return;
    const uploader = this.fileUploader();
    if (uploader) {
      const match = uploader.uploadService.files$$.getValue().find((f) => f.name === target.attachedFilename);
      if (match) {
        uploader.deleteFile(match);
      }
    }
    this.attachments.update((list) => list.filter((_, i) => i !== index));
  }

  protected formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  protected computeUsedBytes(html: string): number {
    const attachmentTotal = this.attachments()
      .filter((a) => a.status === FileUploadStatus.Success)
      .reduce((sum, a) => sum + a.size, 0);

    const referencedUrls = this.extractImgSrcs(html);
    const inlineTotal = this.inlineImages()
      .filter((img) => referencedUrls.has(img.fileUrl))
      .reduce((sum, img) => sum + img.size, 0);

    return attachmentTotal + inlineTotal;
  }

  private extractImgSrcs(html: string): Set<string> {
    if (!html) return new Set();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const urls = new Set<string>();
    doc.querySelectorAll('img').forEach((img) => {
      const src = img.getAttribute('src');
      if (src) urls.add(src);
    });
    return urls;
  }

  onCancel() {
    this.saveDraftIfNeeded();
    this.closeModal();
  }

  private closeModal() {
    this.dialogRef.close();
  }

  private loadSignature(): void {
    toObservable(this.selectedConnectionId, { injector: this.injector })
      .pipe(
        switchMap((connectionId) => {
          if (!connectionId) {
            return of('');
          }
          const protoProvider =
            this.dialogData.provider === EMAIL_PROVIDER_OUTLOOK
              ? ProtoProvider.PROVIDER_OUTLOOK
              : ProtoProvider.PROVIDER_GMAIL;
          return getSignature$({
            namespace: this.dialogData.namespace,
            provider: protoProvider,
            connectionId,
          }).pipe(
            map((resp) => resp.html || ''),
            catchError(() => of('')),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((html) => {
        const editor = this.richTextEditor();

        if (!editor) {
          return;
        }

        const currentContent = this.emailForm.controls.content.value;
        const sigMarker = '<div class="email-signature">';
        const sigIdx = currentContent.indexOf(sigMarker);
        const bodyWithoutSig = sigIdx >= 0 ? currentContent.substring(0, sigIdx).replace(/<br>\s*$/, '') : currentContent;

        if (html) {
          const newContent = `${bodyWithoutSig}<br>${sigMarker}${html}</div>`;
          this.emailForm.controls.content.setValue(newContent);
        } else if (sigIdx >= 0) {
          this.emailForm.controls.content.setValue(bodyWithoutSig);
        }
      });
  }
}
